import { classicResearchFilter, matchedReferenceContext, researchMileage } from './classicResearch.js'
import { formatUtcForPocketBase } from '../contracts/servedRecommendation.v1.js'
import { classicSourceReview, classicReferenceSourceReview } from './classicSourceReview.js'

const literal = value => JSON.stringify(String(value).trim())
export const CLASSIC_SCAN_LIMIT = 2000

export function classicReferenceScope(filters, now = new Date()) {
  classicResearchFilter(filters, now) // Share year/country/price validation with inventory.
  if (!filters.make?.trim()) throw new RangeError('Choose a make for the shortlist')
  return [`make = ${literal(filters.make)}`,
    ...(filters.model?.trim() ? [`model = ${literal(filters.model)}`] : []),
    `year >= ${Number(filters.minYear) - 2}`, `year <= ${Number(filters.maxYear) + 2}`,
    `observed_at >= '${formatUtcForPocketBase(new Date(now.getTime() - 7 * 86400000))}'`,
    `observed_at <= '${formatUtcForPocketBase(now)}'`, 'price > 0'].join(' && ')
}

/** Research priority, NOT the production pricer or a purchase recommendation.
 * An importer first asks whether the cheapest relevant Dutch advert leaves ANY
 * room for costs. A median can hide cheaper competitors. Unknown condition still
 * prevents us converting this observed price difference into expected profit.
 */
export function classicLead(car, referenceRows, { referencesTruncated = false, sourceReviews, now = new Date() } = {}) {
  const identity = value => String(value || '').trim().toLowerCase()
  const scoped = referenceRows.filter(row => identity(row.make) === identity(car.make) && identity(row.model) === identity(car.model))
  const context = matchedReferenceContext(car, scoped)
  const km = researchMileage(car.mileage_km)
  const mileageWindow = km === null ? null : Math.min(75000, Math.max(30000, km * .5))
  const peers = context.rows.filter(row => {
    const refKm = researchMileage(row.evidence?.mileage_km)
    // Adjacent years can straddle a completely different generation (e.g. a
    // classic Spider and its replacement). Broad context stays inspectable,
    // but the shortlist price gap uses the exact advertised year only.
    return Number(row.year) === Number(car.year) && km !== null && refKm !== null && Math.abs(refKm - km) <= mileageWindow
  }).sort((a, b) => Number(a.price) - Number(b.price) || String(a.id).localeCompare(String(b.id)))
  const complete = context.engineMatched && context.fuelMatched && context.bodyMatched && context.gearboxMatched && km !== null
  const sufficient = complete && peers.length >= 3 && !referencesTruncated
  const anchor = sufficient ? Number(peers[0].price) : null
  const ask = Number(car.price_amount_eur)
  const gap = anchor !== null && Number.isFinite(ask) && ask > 0 ? anchor - ask : null
  // An extraordinary discount is a reason to inspect harder, not rank higher.
  const extremeGap = gap !== null && ask < anchor * .5
  const baseStatus = gap === null ? 'evidence' : gap <= 0 ? 'no_room' : extremeGap ? 'condition' : 'investigate'
  return withSourceChecks({ car, context, peers, mileageWindow, anchor, gap, baseStatus, complete, referencesTruncated }, sourceReviews, now)
}

function withSourceChecks(lead, feed, now) {
  const sourceReview = classicSourceReview(lead.car, feed, now)
  const referenceReviews = lead.peers.map(row => classicReferenceSourceReview(row, feed, now))
  // Never discard a failed cheap comparison and quietly move the anchor upwards.
  // That would turn missing evidence into a bigger apparent import opportunity.
  const referenceAttention = referenceReviews.some(row => row && row.status !== 'advert_matched')
  const anchorReviews = lead.peers.flatMap((row, i) => Number(row.price) === lead.anchor ? [referenceReviews[i]] : [])
  const anchorMatched = anchorReviews.length > 0 && anchorReviews.every(row => row?.status === 'advert_matched')
  const status = referenceAttention || (sourceReview && sourceReview.status !== 'advert_matched') ? 'source' : lead.baseStatus
  return { ...lead, sourceReview, referenceReviews, referenceAttention, anchorMatched, status }
}

function compareLeads(a, b) {
  const order = { investigate: 0, condition: 1, evidence: 2, no_room: 3, source: 4 }
  return order[a.status] - order[b.status] ||
    Number(b.anchorMatched && b.sourceReview?.status === 'advert_matched') - Number(a.anchorMatched && a.sourceReview?.status === 'advert_matched') ||
    Number(b.sourceReview?.status === 'advert_matched') - Number(a.sourceReview?.status === 'advert_matched') ||
    (a.status === 'investigate' ? b.gap - a.gap : b.peers.length - a.peers.length) ||
    Number(a.car.price_amount_eur) - Number(b.car.price_amount_eur) || String(a.car.id).localeCompare(String(b.car.id))
}

export function rankClassicLeads(cars, referenceRows, options) {
  const unique = [...new Map(cars.map(car => [`${car.country_code || ''}:${car.listing_id || car.id}`, car])).values()]
  return unique.map(car => classicLead(car, referenceRows, options)).sort(compareLeads)
}

export function refreshClassicSourceChecks(leads, feed, now) {
  return leads.map(lead => withSourceChecks(lead, feed, now)).sort(compareLeads)
}
